import { AlertTriangle, CheckCircle, RefreshCw, ExternalLink } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { validateEnvironment, type EnvironmentValidationResult } from '@/lib/environment-validator';

interface EnvironmentValidatorProps {
  children: React.ReactNode;
}

/**
 * Environment Validation Component
 * 
 * This component validates environment variables on application startup
 * and displays helpful error messages if configuration is missing or invalid.
 */
export function EnvironmentValidator({ children }: EnvironmentValidatorProps) {
  const [validationResult, setValidationResult] = useState<EnvironmentValidationResult | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const performValidation = () => {
    setIsRetrying(true);
    try {
      const result = validateEnvironment();
      setValidationResult(result);
    } catch (error) {
      console.error('Environment validation error:', error);
      setValidationResult({
        isValid: false,
        config: {},
        missingRequired: [],
        missingOptional: [],
        errors: ['Failed to validate environment configuration'],
        warnings: []
      });
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    performValidation();
  }, []);

  // Show loading state while validating
  if (!validationResult) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="text-muted-foreground">Validating environment configuration...</p>
        </div>
      </div>
    );
  }

  // If validation passes, render the app
  if (validationResult.isValid) {
    return children;
  }

  // Show configuration error screen
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
          <CardTitle className="text-2xl">Environment Configuration Required</CardTitle>
          <CardDescription>
            The application cannot start because some environment variables are missing or invalid.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Missing Required Variables */}
          {validationResult.missingRequired.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Missing Required Variables</AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-1">
                  {validationResult.missingRequired.map((variable) => (
                    <div key={variable} className="flex items-center gap-2">
                      <Badge variant="destructive" className="font-mono text-xs">
                        {variable}
                      </Badge>
                    </div>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Configuration Errors */}
          {validationResult.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Configuration Errors</AlertTitle>
              <AlertDescription>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  {validationResult.errors.map((error) => (
                    <li key={error} className="text-sm">{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Configuration Warnings */}
          {validationResult.warnings.length > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Configuration Warnings</AlertTitle>
              <AlertDescription>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  {validationResult.warnings.map((warning) => (
                    <li key={warning} className="text-sm">{warning}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Setup Instructions */}
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Setup Instructions
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-medium">1. Copy the environment template:</p>
                <code className="block bg-background p-2 rounded mt-1 font-mono text-xs">
                  cp .env.example .env
                </code>
              </div>
              
              <div>
                <p className="font-medium">2. Get your Supabase credentials:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                  <li>Go to <a href="https://supabase.com/dashboard" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                    Supabase Dashboard <ExternalLink className="h-3 w-3" />
                  </a></li>
                  <li>Select your project</li>
                  <li>Navigate to Settings → API</li>
                  <li>Copy your Project URL and anon/public key</li>
                </ul>
              </div>
              
              <div>
                <p className="font-medium">3. Update your .env file:</p>
                <div className="bg-background p-2 rounded mt-1 font-mono text-xs space-y-1">
                  <div>VITE_SUPABASE_URL=https://your-project-id.supabase.co</div>
                  <div>VITE_SUPABASE_PUBLISHABLE_KEY=your-anon-key</div>
                  <div>VITE_SUPABASE_PROJECT_ID=your-project-id</div>
                </div>
              </div>
              
              <div>
                <p className="font-medium">4. Restart your development server:</p>
                <code className="block bg-background p-2 rounded mt-1 font-mono text-xs">
                  npm run dev
                </code>
              </div>
            </div>
          </div>

          {/* Current Configuration Status */}
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-semibold mb-3">Current Configuration Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-green-600 mb-2">✓ Found Variables:</p>
                <div className="space-y-1">
                  {Object.keys(validationResult.config).map((key) => (
                    <Badge key={key} variant="secondary" className="font-mono text-xs mr-1 mb-1">
                      {key}
                    </Badge>
                  ))}
                  {Object.keys(validationResult.config).length === 0 && (
                    <p className="text-muted-foreground">None</p>
                  )}
                </div>
              </div>
              
              <div>
                <p className="font-medium text-red-600 mb-2">✗ Missing Variables:</p>
                <div className="space-y-1">
                  {[...validationResult.missingRequired, ...validationResult.missingOptional].map((key) => (
                    <Badge key={key} variant="outline" className="font-mono text-xs mr-1 mb-1">
                      {key}
                    </Badge>
                  ))}
                  {validationResult.missingRequired.length === 0 && validationResult.missingOptional.length === 0 && (
                    <p className="text-muted-foreground">None</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={performValidation}
              disabled={isRetrying}
              variant="outline"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Check Again
                </>
              )}
            </Button>
            
            <Button asChild>
              <a 
                href="https://supabase.com/dashboard" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Supabase Dashboard
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}